import React from "react";
import "./RecordsPagination.scss";
import { TABLE } from "../../../constants/common";

import { ReactComponent as ChevronsLeftIcon } from "../../../assets/icons/patient-layout-v2/records/ChevronsLeftIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../../../assets/icons/patient-layout-v2/records/ChevronLeftIcon.svg";
import { ReactComponent as ChevronRightIcon } from "../../../assets/icons/patient-layout-v2/records/ChevronRightIcon.svg";
import { ReactComponent as ChevronsRightIcon } from "../../../assets/icons/patient-layout-v2/records/ChevronsRightIcon.svg";
import Button from "../../../atoms/Button/Button";

interface Props {
  totalRecords: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  dataLength?: number;
}

const RecordsPagination: React.FC<Props> = ({
  totalRecords,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  dataLength = 0,
}) => {
  const totalPages = Math.ceil(totalRecords / pageSize);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const delta = 1;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    const startPage = Math.max(2, currentPage - delta);
    const endPage = Math.min(totalPages - 1, currentPage + delta);

    if (startPage > 2) pages.push("ellipsis-start");
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    if (endPage < totalPages - 1) pages.push("ellipsis-end");
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  return (
    <div className="records-pagination">
      <div className="records-pagination-size">
        <span>Items per page</span>
        <select
          value={pageSize}
          disabled={dataLength === 0}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {TABLE.PAGINATION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="records-pagination-summary">
          showing {Math.min((currentPage - 1) * pageSize + 1, totalRecords)}–
          {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
        </span>
      </div>

      <div className="records-pagination-nav">
        <Button
          variant="text"
          size="sm"
          rounded="full"
          disabled={currentPage === 1}
          onClick={() => onPageChange(1)}
          iconStart={<ChevronsLeftIcon />}
        />
        <Button
          variant="text"
          size="sm"
          rounded="full"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          iconStart={<ChevronLeftIcon />}
        />
        {getPageNumbers().map((page, index) =>
          typeof page === "string" ? (
            <span className="page-ellipsis" key={`${page}-${index}`}>
              …
            </span>
          ) : (
            <Button
              key={page}
              variant={currentPage === page ? "contained" : "outlined"}
              size="sm"
              rounded="full"
              onClick={() => onPageChange(page)}
            >
              {page}
            </Button>
          ),
        )}
        <Button
          variant="text"
          size="sm"
          rounded="full"
          disabled={currentPage === totalPages || dataLength === 0}
          onClick={() => onPageChange(currentPage + 1)}
          iconStart={<ChevronRightIcon />}
        />
        <Button
          variant="text"
          size="sm"
          rounded="full"
          disabled={currentPage === totalPages || dataLength === 0}
          onClick={() => onPageChange(totalPages)}
          iconStart={<ChevronsRightIcon />}
        />
      </div>
    </div>
  );
};

export default RecordsPagination;
